/*global SVP*/
'use strict';
import Sizzle from 'sizzle';

import Config from '../config';
import resolveStreamingConfig from './resolve-streaming-config';

import metadataFetchFromSource from '../api/metadata-fetch-from-source';

import device from '../util/device';
import * as domUtils from '../util/dom';
import SVPCustomEvent from '../util/custom-event';
import debugUtil from '../util/debug';
import VideoType from '../constants/video-type';
import ErrorType from '../constants/error-type';

import dash from './dash-video';
import hls from './hls-video';

const Debug = debugUtil('wrapperApi');

const NAMESPACE = 'wrapper';
const READY_NAMESPACE = 'wrapper-ready';

const STATE_CLASS = {
	forceHide: 'svp_is--force-hide'
};

function applyFunctionToFilters(filters, fnName, args) {
	if (!filters) {
		return true;
	}

    for(let filter of filters) {
        if (hasFunction(filter, fnName)) {
            let retValue = filter[fnName].apply(filter, args);

            if (isValidReturnValue(retValue, fnName)) {
                return retValue;
            }
        }
    }

	return null;
}

function hasFunction(filter, fnName) {
    return filter && typeof filter[fnName] === 'function';
}

function isValidReturnValue(retValue, fnName) {
    return (fnName.match(/^is/) || retValue !== false) && retValue !== null && retValue !== undefined;
}

function hasSourceElementChild(element) {
    return Sizzle('source', element).length > 0;
}

function getEvents(videoObject) {
	// TODO: Which of these do we need/want to support?
	let events = [
		'abort',
        'bufferEmpty',
		'canplay',
		'canplaythrough',
		'durationchange',
		'emptied',
		'ended',
		'error',
		'loadeddata',
		'loadedmetadata',
		'loadstart',
		'pause',
		'play',
		'playing',
		'progress',
		'ratechange',
		'resize',
		'seeked',
		'seeking',
		'stalled',
		'suspend',
		'timeupdate',
		'volumechange',
		'waiting'
	];

	if (videoObject.customEvents) {
		events = events.concat(videoObject.customEvents);
	}

	return events;
}

function cleanEvents(videoObject) {
	if (videoObject.removeEventSVP) {
		getEvents(videoObject).forEach((eventName) => {
			videoObject.removeEventSVP(NAMESPACE + '.' + eventName);
		});
	}
}

function attachEvents(rootElement, videoObject) {
	if (videoObject.addEventSVP) {
		getEvents(videoObject).forEach((eventName) => {
			videoObject.addEventSVP(NAMESPACE + '.' + eventName, (event) => {
				if (event.preventDefault) {
					event.preventDefault();
				}

				if (eventName !== 'timeupdate') {
				    Debug('received event: ' + eventName);
                }

				rootElement.dispatchEvent(new SVPCustomEvent(event.type, { detail: event }));

				return false;
			});
		});
	}
}

function handleError(wrapperApi) {
	wrapperApi.on(ErrorType.UNKNOW_VIDEO_TYPE_ERROR, function (e) {
		// If no plugin has handled this, send an error
		if (!e.defaultPrevented) {
			wrapperApi.fire('error', {
				name: ErrorType.UNKNOW_VIDEO_TYPE_ERROR
			});
		}

		wrapperApi.off(ErrorType.UNKNOW_VIDEO_TYPE_ERROR);
	});

	wrapperApi.fire(ErrorType.UNKNOW_VIDEO_TYPE_ERROR, null, {
        cancelable: true
    });
}

function _videoObjectReadyFn(wrapperApi, videoObject, isExternalUi) {
	if (!wrapperApi.state()) {
		return;
	}
	wrapperApi.state().loading = false;
	wrapperApi.state().ready = true;
	wrapperApi._data._video = videoObject;

	domUtils.removeEvents(videoObject, 'loadstart', READY_NAMESPACE);

	wrapperApi.fire('ready', {
		videoId: wrapperApi.data() && wrapperApi.data().videoId,
		externalUi: isExternalUi
	});

	if (Config.resetOnEnd) {
		wrapperApi.on('reset.ended', function () {
			var videoId = wrapperApi.data() && wrapperApi.data().videoId;
			wrapperApi.off('reset.ended');

			if (videoId) {
				wrapperApi.on('reset.seeked', function () {
					wrapperApi.off('reset.seeked');
					wrapperApi.pause();
				});

				setTimeout(function () {
					wrapperApi.reset();
					wrapperApi.fire('ready', {
						videoId: videoId,
						externalUi: isExternalUi
					});
				}, 100);
			}
		});
	}

}

function _setup(wrapperApi, videoId, metadata, videoUrls, textUrls) {
	if (!wrapperApi.state()) {
		return;
	}
	wrapperApi._data.videoId = videoId;
	wrapperApi.fire('metadata', metadata);

	let videoObject;
    let videoElement = Sizzle('video', wrapperApi.element())[0];
    const streamingConfig = resolveStreamingConfig(videoUrls, textUrls, metadata, videoElement);

	if (window.SVP._inSweden !== undefined) {
		if (!window.SVP._inSweden && (metadata.geoBlockedSweden || (metadata.rights && metadata.rights.geoBlockedSweden))) {
			//TODO rewrite this once we decide how to handle rights in the api
			wrapperApi.fire('error', { name: ErrorType.GEO_BLOCK_ERROR });

			return;
		}
	}

	const isNewType = wrapperApi.data().videoConfig.videoType !== streamingConfig.videoType;
	if (wrapperApi.video().unload && isNewType) {
		Debug('Switching video format: ' + wrapperApi.data().videoConfig.videoType + ' --> ' + streamingConfig.videoType);
		cleanEvents(wrapperApi.video());
		wrapperApi.video().unload();
		videoElement = Sizzle('video', wrapperApi.element())[0];
	}

    const autoplay = wrapperApi.isAutoplay();
    if (autoplay) {
        videoElement.setAttribute('autoplay', '');
        videoElement.autoplay = true;
    } else {
        videoElement.removeAttribute('autoplay');
        videoElement.autoplay = false;
    }

	switch (streamingConfig.videoType) {
		case VideoType.DASH: {
            require.ensure([], (require) => {
                const dashjs = require('dashjs')['default'];
                videoObject = dash.load(videoId, videoElement, streamingConfig.videoUrl, metadata, wrapperApi.config, wrapperApi, dashjs);

                domUtils.addEvents(videoObject, 'loadstart', () => {
                    _videoObjectReadyFn(wrapperApi, videoObject, false);
                }, READY_NAMESPACE);

                if (isNewType) {
                    attachEvents(wrapperApi.element(), videoObject);
                }
            }, 'dash');
            break;
        }
		case VideoType.HLS: {
            videoObject = hls.load(videoElement, streamingConfig.videoUrl, metadata, wrapperApi.config, wrapperApi);

            domUtils.addEvents(videoObject, 'loadstart', () => {
                _videoObjectReadyFn(wrapperApi, videoObject, false);
            }, READY_NAMESPACE);

            if (isNewType) {
                // On Android 4.4.2 and below, the poster image sometimes isn't removed properly. Seeking fixes this.
                if (device.isBrokenAndroidChrome()) {
                    domUtils.addEvents(wrapperApi.element(), 'timeupdate', () => {
                        if (wrapperApi.getCurrentTime() > 0) {
                            domUtils.removeEvents(wrapperApi.element(), 'timeupdate', 'androidWrapper');
                            wrapperApi.seek(Math.floor(wrapperApi.getCurrentTime()));
                        }
                    }, 'androidWrapper');
                }
                attachEvents(wrapperApi.element(), videoObject);
            }
			break;
        }
		case VideoType.HDS:
		case VideoType.FLASHLS: {
		    const oldVideoObject = wrapperApi._data._video;
			videoObject = Config.flashEngine.load(videoElement, streamingConfig.videoUrl, streamingConfig.textUrl, metadata, autoplay);

			if (oldVideoObject) {
                cleanEvents(oldVideoObject); //clear events on old obj
            }
            attachEvents(wrapperApi.element(), videoObject);

			_videoObjectReadyFn(wrapperApi, videoObject, false);
            if (wrapperApi.isAutoplay()) {
                wrapperApi.play();
            }
			break;
        }
		default: {
			handleError(wrapperApi);
			_videoObjectReadyFn(wrapperApi, videoElement, true);
			if (isNewType) {
				attachEvents(wrapperApi.element(), videoElement);
			}
			break;
        }
	}
	wrapperApi._data.videoConfig = streamingConfig;
}


var Props = function (videoId, rootElement) {
	this.videoId = videoId;
	this.allowNoUi = true;
	this.isRemote = false;
	this._video = {};
	this._root = rootElement;

	this.videoConfig = {
		videoType: VideoType.UNKNOWN
	};
};

Props.prototype.destroy = function () {
	//Set all references to undefined
	var key;
	for (key in this) {
		if (Object.prototype.hasOwnProperty.call(this, key)) {
			this[key] = undefined;
		}
	}
};

var State = function () {
	this.ready = false;
	this.loading = false;
	this.hidden = false;
};

export default class WrapperAPI {
	constructor(videoId, rootElement, config) {
		this._data = new Props(videoId, rootElement);
		this._state = new State();
		this.filters = [];
		this.device = device;
		this.config = config;
		this.fullscreenElement = null;
		this.autoplay = this.config.autoplay;
	}

	data() {
		return this._data;
	}

	state() {
		return this._state;
	}

	element() {
		return this._data && this._data._root;
	}

	video() {
		return this._data && this._data._video;
	}

	isAutoplay() {
	    return this.autoplay;
    }

    load(videoId, autoplay) {
        if (videoId) {
            this._data.videoId = videoId;
        }

        Debug('load(videoId, autoplay)', this._data.videoId, autoplay);

        this._state.ready = false;
        this._state.loading = true;

        this.autoplay = autoplay !== undefined ? autoplay : this.config.autoplay;

		if (applyFunctionToFilters(this.filters, 'load', arguments)) { // eslint-disable-line prefer-rest-params
			return;
		}

		const callback = _setup.bind(this, this, this._data.videoId);
		const errorHandler = (error, data) => {
			// TODO: this should/could be made more fine-grained (errType is http/timeout/error)
			if (data) {
				this.fire('metadata', data);
			}
			this.fire('error', error);
		};

		const rootElement = this.element();

		if (hasSourceElementChild(rootElement)) {
			metadataFetchFromSource(rootElement, callback);
		} else {
			Config.metadataFetchFunction(this._data.videoId, callback, errorHandler);
		}
	}

    /**
     * Attempts to start the playback of the video.
     *
     * @return {Promise} Returns a Promise that is fulfilled when the video is
     * started or rejected if any error occured.
     */
	play() {
		if (this.state().ready) {
			if (applyFunctionToFilters(this.filters, 'play', arguments)) { // eslint-disable-line prefer-rest-params
				return Promise.reject(new Error('Interrupted by filters.'));
			}

            // The return value of play() varies between browsers. If a
            // promise is returned we pass that as our return value, otherwise
            // we make a guess and resolve a promise directly.
            const result = this.video().play();
            if (typeof result === 'object' && typeof result.then === 'function') {
                return result;
            } else {
                return Promise.resolve();
            }

		} else if (this.state().loading) {
            // The video is not yet ready to be played. We wait for a loadstart
            // (which occurs when the video is being loaded) before we execute
            // play. Note that we create a wrapping promise that resolves with
            // the inner play promise.
            return new Promise((resolve) => {
                const onLoadStart = () => {
                    this.element().removeEventSVP('loadstart', onLoadStart);
                    resolve(this.play());
                };
                this.element().addEventSVP('loadstart', onLoadStart);
            });
		}
	}

	destroy() {
		if (this.video().unload) {
			this.video().unload();
		}
		cleanEvents(this.video());

		this._data.destroy();
		this._data = undefined;
		this._state = undefined;
		this.filters = undefined;
		this.config = undefined;
		this.device = undefined;

		var noop = function () {
		    // empty
        };
		this.load = noop;
		this.play = noop;
		this.destroy = noop;
		this.on = noop;
		this.off = noop;
		this.fire = noop;
	}

    /**
     * Dispatch an event on the wrapper element
     *
     * @param {string} eventName - The name of the event.
     * @param {object} [data] - The detail data for the CustomEvent.
     * @param {object} [customEventData] - EventInit dictionary to pass to the
     *      CustomEvent. It can specify "bubbles", "cancelable" and "compsed"
     *      fields on the event.
     */
	fire(eventName, data, customEventInit) {
        const el = this.element();
        if (!el) {
            return;
        }

        const ev = new SVPCustomEvent(
            eventName,
            Object.assign({ detail: data }, customEventInit)
        );

        const cancelled = el.dispatchEvent(ev);

        return cancelled;
	}

	on() {
		return this.element() && this.element().addEventSVP && this.element().addEventSVP.apply(this.data()._root, arguments); // eslint-disable-line prefer-rest-params
	}

	off() {
		return this.element() && this.element().removeEventSVP && this.element().removeEventSVP.apply(this.data()._root, arguments); // eslint-disable-line prefer-rest-params
	}

	once(event, callback) {
		var _this = this;
		event = event.match(/\./) ? event : NAMESPACE + '.' + event;

		return this.on(event, function () {
			callback.apply(_this, arguments); // eslint-disable-line prefer-rest-params
			_this.off(event);
		});
	}

	show() {
		domUtils.removeClass(this.element(), STATE_CLASS.forceHide);
		this.state().hidden = false;
	}

	hide() {
		domUtils.addClass(this.element(), STATE_CLASS.forceHide);
		this.state().hidden = true;
	}

	setFullscreenElement(element) {
	    // Fullscreen API in iOS is not supported for other node types than video
	    if (device.isIOS() && element.tagName.toLocaleLowerCase() !== 'video') {
	        return false;
        }

	    this.fullscreenElement = element;

	    return true;
    }

    getFullscreenElement() {
	    return this.fullscreenElement || this.element();
    }

    enterFullscreen() {
	    domUtils.enterVideoFullscreen(this);
    }

    exitFullscreen() {
	    domUtils.exitVideoFullscreen(this);
    }
}

function applyFunctionToFiltersOrVideoObject(api, fnName, args) {
    let ret = applyFunctionToFilters(api.filters, fnName, args);
    if (ret === null || ret === undefined) {
        const videoObject = api.video();
        if (videoObject[fnName]) {
            ret = videoObject[fnName].apply(videoObject, args);
        }
    }

    return ret;
}

['pause', 'seek', 'insertButton', 'showCaptions', 'reset', 'setVolume', 'mute', 'setAudioTrack']
    .forEach((fnName) => {
        WrapperAPI.prototype[fnName] = function () {
            applyFunctionToFiltersOrVideoObject(this, fnName, arguments); // eslint-disable-line prefer-rest-params

            return this;
        };
    });

['getCurrentTime', 'getDuration', 'getVolume', 'getMaxSeekable', 'isMuted', 'getMetrics', 'getSrc', 'isPaused', 'isSeeking', 'getTextTracks', 'getAudioTracks']
    .forEach((fnName) => {
        WrapperAPI.prototype[fnName] = function () {
			return applyFunctionToFiltersOrVideoObject(this, fnName, arguments); // eslint-disable-line prefer-rest-params
		};
	});



// WEBPACK FOOTER //
// ./src/js/modules/wrapperApi.js